import { inTrial, monthlyCharge, todayIso, type Charge, type Pricing } from "@szerviz/core";
import type { OpActivity, OpAiUsage, OpSubscription, OpWorkshop } from "../data";
import { opSubscriptionState } from "../data/subscription";

/** Tags offered as one click on the operator pages. */
export const SUGGESTED_TAGS = ["VIP", "teszt", "partner"];

/**
 * Signs of a subscription shared by many or used only for the AI: many AI calls or many
 * new vehicles this month, but few work orders.
 */
export function oddUse(u: OpAiUsage | undefined, a: OpActivity | undefined): boolean {
  const orders = a?.workOrders ?? 0;
  const calls = u?.calls ?? 0;
  const vehicles = a?.vehicles ?? 0;
  return (calls >= 20 && calls > 3 * orders) || (vehicles >= 20 && vehicles > 3 * orders);
}

export const seatText = (a: OpActivity | undefined) => (!a ? "–" : a.seatLimit >= 100000 ? `${a.members} / ∞` : `${a.members} / ${a.seatLimit}`);

/** The real AI cost is too high when it passes the set share of the monthly fee (or there is no fee). */
export function aiCostAlert(u: OpAiUsage | undefined, fee: number | null, alertShare: number): boolean {
  if (!u || u.costHuf <= 0) return false;
  return fee === null || fee <= 0 || u.costHuf > fee * alertShare;
}

/**
 * What to invoice a workshop for this calendar month (packages/core monthlyCharge): the subscription period
 * starting in it, this month's SMS (op_workshops) and the extra AI credit bought (op_ai_usage; gifts never).
 */
export function invoiceOf(w: OpWorkshop, sub: OpSubscription | undefined, ai: OpAiUsage | undefined, pricing: Pricing, today = todayIso()): Charge & { periodStart: string | null } {
  return monthlyCharge(opSubscriptionState(w, sub), pricing, today, { smsNet: w.smsAmount, aiCreditGross: ai?.boughtHuf ?? 0 });
}

/** Platform totals of the subscriptions: in trial, founders, yearly, and this month's invoices. */
export function subscriptionTotals(rows: OpWorkshop[], subs: Map<string, OpSubscription>, ai: Map<string, OpAiUsage>, pricing: Pricing, today = todayIso()) {
  let net = 0;
  let gross = 0;
  let invoices = 0;
  let trial = 0;
  let founders = 0;
  let yearly = 0;
  for (const w of rows) {
    const s = subs.get(w.id);
    if (s && inTrial(s, today)) trial++;
    if (s?.founder && !w.tags.includes("teszt")) founders++;
    if (s?.period === "yearly") yearly++;
    const c = invoiceOf(w, s, ai.get(w.id), pricing, today);
    if (c.gross > 0) invoices++;
    net += c.net;
    gross += c.gross;
  }
  return { net, gross, invoices, trial, founders, yearly };
}
